/* eslint-disable react/prop-types */
import { Modal, Tabs } from 'antd';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useIsFwe } from '../../../../hooks/useIsFwe';
import useAnalyticsStore from '../../../../store/analyticsStore';
import useAuthStore from '../../../../store/authStore';
import useHomeStore from '../../../../store/homeStore';
import { useMapLayerStore } from '../../../../store/layerStore';
import useLegendStore from '../../../../store/legendStore';
import { useMarkerStore } from '../../../../store/markerStore';
import { usePointStore } from '../../../../store/pointStore';
import { MarkerItem } from '../../../../types/home';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import { IMarker } from '../../../../types/marker';
import { UserGroupPermissions } from '../../../../types/userGroupPermissions';
import { analyticsUserInput } from '../../../../utils/analyticsUserInput';
import { ITEM, TabKey } from '../../../../utils/common';
import { useIsTnw, useSignalR } from '../../../../utils/hooks';
import AnalyticsPanel from '../../Analytics/components/AnalyticsPanel';
import { useAdvancedAnalytics } from '../../Analytics/hooks/useAdvanceAnalytics';
import { useAnalyticsCommonInput } from '../../Analytics/hooks/useAnalyticsCommonInput';
import { useAnalyticsNavigation } from '../../Analytics/hooks/useAnalyticsNavigation';
import { useBasicAnalyticsData } from '../../Analytics/hooks/useBasicAnalyticsData';
import { useExtractionData } from '../../Analytics/hooks/useExtractionData';
import { useMetadata } from '../../Analytics/hooks/useMetadata';
import { useRosePlot } from '../../Analytics/hooks/useRosePlot';
import { useScatterPlot } from '../../Analytics/hooks/useScatterPlot';
import { useScatterTable } from '../../Analytics/hooks/useScatterTable';
import { useTimeSeriesPlot } from '../../Analytics/hooks/useTimeSeriesPlot';
import ReportsPanel from '../../Reports/ReportsPanel';
import { useInsituAvailability } from '../../Validation/hooks/useInsituAvailability';
import { usePointSelection } from '../../Validation/hooks/usePointSelection';
import { useValidationTabs } from '../../Validation/hooks/useValidationTabs';
import ValidationPanel from '../../Validation/ValidationPanel';
import styles from '../Style/AnalyticsContainer.module.scss';

interface AnalyticsContainerProps {
  activeTab: TabKey;
  setActiveTab: (key: TabKey) => void;
  isPopupVisible: boolean;
  setIsPopupVisible: (visible: boolean) => void;
  switchVisiblePopup: (id: string) => void;
  activePoint?: MarkerItem;
  setDatasetIsOfflineWarningVisible: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  onMeshControl: (value: string) => void;
  isTnw: boolean;
  point?: MarkerItem;
  gridCellSize: string;
  waterDepth: string;
  userGroupPermissions: UserGroupPermissions;
  category: string | string[];
  activePlotValidation: string[];
  setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>;
  onChangePointLocation: (coords: number[], forceChange?: boolean) => void;
  onChangeActiveKey: (item: string | string[], tabKey: TabKey) => void;
  setShowLogin: (show: boolean) => void;
  isAuthorized: boolean;
}

interface TabContentProps {
  tabKey: TabKey;
  datasetId: string;
  activePoint?: IMarker;
  setDatasetIsOfflineWarningVisible: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  onMeshControl: (value: string) => void;
  isTnw: boolean;
  point?: IMarker;
  gridCellSize: string;
  waterDepth: string;
  userGroupPermissions: UserGroupPermissions;
  category: string | string[];
  activePlotValidation: string[];
  setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>;
  onChangePointLocation: (coords: number[], forceChange?: boolean) => void;
  onChangeActiveKey: (item: string | string[], key: TabKey) => void;
}

export interface AnalyticsHandle {
  cancelAll: () => void;
}

const TabContent = forwardRef<AnalyticsHandle, TabContentProps>(
  function TabContent(
    {
      tabKey,
      activePoint,
      setDatasetIsOfflineWarningVisible,
      onMeshControl,
      datasetId,
      point,
      gridCellSize,
      waterDepth,
      userGroupPermissions,
      category,
      activePlotValidation,
      setActivePlotValidation,
      onChangePointLocation,
      onChangeActiveKey,
    },
    ref
  ) {
    const [analyticsDefinition, setAnalyticsDefinition] = useState('');
    const [isForAllAnalytics, setIsForAllAnalytics] = useState(false);
    const [analyticsDateTime, setAnalyticsDateTime] = useState<any[]>([]);
    const enrichedMetadata = useAnalyticsStore((s) => s.enrichedMetadata);
    const setEnrichedMetadata = useAnalyticsStore(
      (state) => state.setEnrichedMetadata
    );
    const user = useAuthStore((s) => s.user);
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const setShowLogin = useHomeStore((s) => s.setShowLogin);
    const timeStepInterval = useHomeStore((s) => s.timeStepInterval);
    const showMesh = useLegendStore((s) => s.showMesh);
    const markers = useMarkerStore((s) => s.markers);
    const updateMarker = useMarkerStore((s) => s.updateMarker);
    const setActiveDatasetKey = useMarkerStore((s) => s.setActiveDatasetKey);

    const isTnw = useIsTnw(datasetId) && user.authenticated;
    const isFwe = useIsFwe(datasetId);

    const isOfflineDataset =
      point?.isDatasetOffline?.[category as string] ?? true;
    const isAnalytics = tabKey === ITEM.ANALYTICS;
    const isValidation = tabKey === ITEM.VALIDATION;

    const showErrorFromSignalR = useRef<boolean>(false);

    const { messageReceived, receivedMessageText, resetMessageState } =
      useSignalR('AnalyticsReceiveMessage', 'JobResult');

    const [userInput, updateUserInput] = useReducer(
      (state: IAnalytcisUserInput, update: Partial<IAnalytcisUserInput>) => ({
        ...state,
        ...update,
      }),
      analyticsUserInput
    );

    const cancelAllOperation = () => {
      tsPlot.cancelUpdateTS();
      rosePlot.cancelRosePlot();
      scatterPlot.cancelScatterPlot();
      scatterTable.cancelScatterTable();
      advancedAnalytics.cancelAdvancedAnalytics();
    };

    useEffect(() => {
      if (messageReceived) {
        const splitMessage = receivedMessageText.split(':');
        let hubMessage: string;

        if (splitMessage.length > 1) {
          hubMessage = splitMessage[splitMessage.length - 1];
        } else {
          hubMessage = receivedMessageText;
        }

        if (hubMessage && hubMessage.toLowerCase() !== 'success') {
          advancedAnalytics.setErrorMessage(
            advancedAnalytics.advancedAnalyticType,
            hubMessage
          );
        }
        showErrorFromSignalR.current = true;
        resetMessageState();
      } else {
        showErrorFromSignalR.current = false;
      }
    }, [messageReceived]);

    const metadata = useMetadata(
      datasetId,
      setDatasetIsOfflineWarningVisible,
      setAnalyticsDefinition
    );

    const navigation = useAnalyticsNavigation(
      analyticsDefinition,
      userGroupPermissions,
      user,
      isTnw,
      isAuthenticated,
      activePoint?.coordinates!,
      datasetId,
      setShowLogin,
      isAnalytics,
      cancelAllOperation
    );

    const { orderId, isLoading: isLoadingExtraction } = useExtractionData({
      datasetId,
      coordinates: activePoint?.coordinates,
      pointLabel: activePoint?.label,
      gridCellSize,
      waterDepth,
      timeStepInterval,
      metadata: metadata.metadata,
      isAdvanceAnalyticsOrValidation:
        navigation.isAdvanceAnalytics ||
        isValidation ||
        datasetId.includes('CMEMS'),
      onDataExtracted: setEnrichedMetadata,
    });

    // ANALYTICS SECTION
    const basicData = useBasicAnalyticsData(
      {
        previewDatasetId: datasetId,
        coordinates: activePoint?.coordinates!,
        pointName: activePoint?.label!,
        gridCellSize,
        waterDepth,
        point,
        setDatasetIsOfflineWarningVisible,
        isOffline: isOfflineDataset,
        isFwe,
        fwePointName: point?.fwePointName,
        metadata: metadata.metadata,
        orderId: orderId,
      },
      { enabled: isAnalytics }
    );

    const tsPlot = useTimeSeriesPlot({
      coordinates: activePoint?.coordinates!,
      timeStepInterval,
      pointLabel: point?.label || '',
      isFwe,
      fwePointName: point?.fwePointName,
      setTimeSeriesPreview: basicData.setTimeSeriesPreview,
    });

    const rosePlot = useRosePlot({
      coordinates: activePoint?.coordinates!,
      timeStepInterval,
      pointLabel: point?.label || '',
      isFwe,
      fwePointName: point?.fwePointName,
      setRosePlotPreview: basicData.setRosePlotPreview,
    });

    const scatterPlot = useScatterPlot({
      coordinates: activePoint?.coordinates!,
      timeStepInterval,
      pointLabel: point?.label || '',
      isFwe,
      fwePointName: point?.fwePointName,
      setScatterPlotPreview: basicData.setScatterPlotPreview,
    });

    const advancedAnalytics = useAdvancedAnalytics(
      activePoint?.coordinates!,
      isFwe,
      point?.fwePointName
    );

    const scatterTable = useScatterTable(activePoint?.coordinates!);

    const commonInput = useAnalyticsCommonInput({
      downloadItems: activePoint?.downloadPage!,
      category,
      coordinates: activePoint?.coordinates!,
      previewDatasetId: datasetId,
      point,
      onChangeActiveKey,
      enrichedMetadata,
      showMesh,
      onMeshControl,
      updateMarker,
      markers,
      setActiveDatasetKey,
      setDatasetIsOfflineWarningVisible,
    });

    const analyticsDeps = {
      basicData,
      tsPlot,
      rosePlot,
      scatterPlot,
      advancedAnalytics,
      scatterTable,
      commonInput,
      navigation,
      isFwe,
    };

    // VALIDATION SECTION
    const showInSituPoints = useLegendStore((s) => s.showInSituPoints);
    const datasetZones = useMapLayerStore((s) => s.datasetZones);
    const inSituPoints = usePointStore((s) => s.inSituPoints);

    const { activeValidationTab, handleChangeTab } = useValidationTabs(
      activePoint,
      datasetId
    );
    const selectedDatazone =
      datasetZones && datasetZones.find((item) => item.id === datasetId);

    const { isInsituAvailable, disabledChart } = useInsituAvailability(
      selectedDatazone,
      inSituPoints,
      activePoint,
      showInSituPoints,
      datasetId,
      isValidation
    );

    const { pointOptions, handleChangePoint } = usePointSelection(
      markers,
      onChangeActiveKey
    );

    const validationDeps = {
      isLoadingExtraction,
      advancedAnalytics,
      activeValidationTab,
      handleChangeTab,
      isInsituAvailable,
      disabledChart,
      pointOptions,
      handleChangePoint,
    };

    useEffect(() => {
      advancedAnalytics.resetAdvancedAnalytics();
      scatterTable.resetScatterTable();
      tsPlot.resetTSPlot();
      rosePlot.resetRosePlot();
      scatterPlot.resetScatterPlot();
      updateUserInput({ ...analyticsUserInput });
      setIsForAllAnalytics(false);
      setAnalyticsDateTime([]);
    }, [datasetId]);

    useImperativeHandle(
      ref,
      () => ({
        cancelAll: () => {
          cancelAllOperation();
        },
      }),
      []
    );

    switch (tabKey) {
      case ITEM.VALIDATION:
        return (
          <ValidationPanel
            datasetId={datasetId}
            key={`validation-panel-${activePoint?.id}`}
            category={category as string}
            activePlotValidation={activePlotValidation}
            setActivePlotValidation={setActivePlotValidation}
            activePoint={activePoint}
            cellSize={gridCellSize}
            waterDepth={waterDepth}
            userGroupPermissions={userGroupPermissions}
            onChangePointLocation={onChangePointLocation}
            onMeshControl={onMeshControl}
            onChangeActiveKey={onChangeActiveKey}
            validationDeps={validationDeps}
            userInput={userInput}
            updateUserInput={updateUserInput}
            setDatasetIsOfflineWarningVisible={
              setDatasetIsOfflineWarningVisible
            }
          />
        );
      case ITEM.ANALYTICS:
        return (
          <AnalyticsPanel
            category={category}
            key={`analytics-panel-${activePoint?.id}`}
            coordinates={activePoint?.coordinates!}
            previewDatasetId={datasetId}
            userGroupPermissions={userGroupPermissions}
            pointProps={
              activePoint?.coordinates[0] +
              '_' +
              activePoint?.coordinates[1] +
              '_' +
              waterDepth +
              '_' +
              gridCellSize
            }
            pointName={activePoint ? activePoint.label : ''}
            gridCellSize={gridCellSize.toString()!}
            waterDepth={waterDepth.toString()!}
            point={point}
            onChangeActiveKey={onChangeActiveKey}
            userInput={userInput}
            updateUserInput={updateUserInput}
            analyticsDeps={analyticsDeps}
            isForAllAnalytics={isForAllAnalytics}
            setIsForAllAnalytics={setIsForAllAnalytics}
            analyticsDateTime={analyticsDateTime}
            setAnalyticsDateTime={setAnalyticsDateTime}
          />
        );
      case ITEM.REPORTS:
        return (
          <ReportsPanel
            datasets={commonInput.categoryDataset}
            pointName={activePoint ? activePoint.label : ''}
            userGroupPermissions={userGroupPermissions}
            coordinates={activePoint?.coordinates!}
            waterDepth={waterDepth}
            point={point}
            handleChangePoint={(item) => onChangeActiveKey(item, ITEM.REPORTS)}
            pointOption={analyticsDeps.commonInput.pointOptions}
            setDatasetIsOfflineWarningVisible={
              setDatasetIsOfflineWarningVisible
            }
          />
        );
      default:
        return null;
    }
  }
);

const TabContentMemoized = React.memo(TabContent);

export const AnalyticsContainer: React.FC<AnalyticsContainerProps> = React.memo(
  function BaseAnalyticsContainer({
    isPopupVisible,
    setIsPopupVisible,
    activePoint,
    setDatasetIsOfflineWarningVisible,
    onMeshControl,
    isTnw,
    point,
    gridCellSize,
    waterDepth,
    userGroupPermissions,
    category,
    activePlotValidation,
    setActivePlotValidation,
    onChangePointLocation,
    activeTab,
    setActiveTab,
    onChangeActiveKey,
    switchVisiblePopup,
    setShowLogin,
    isAuthorized,
  }) {
    const analyticsRef = useRef<AnalyticsHandle | null>(null);

    const datasetId =
      activePoint?.downloadPage.find(
        (item) => item.radioKey === (category as string)
      )?.datasetId ?? '';

    const handleTabChange = useCallback(
      (key: string) => {
        if (key === ITEM.VALIDATION && !isAuthorized) {
          setShowLogin(true);
          return;
        }
        if (key === ITEM.REPORTS && !isAuthorized) {
          setShowLogin(true);
          return;
        }
        analyticsRef.current?.cancelAll();
        setActiveTab(key as TabKey);
      },
      [isAuthorized, setActiveTab, setShowLogin]
    );

    const handleCancel = useCallback(() => {
      analyticsRef.current?.cancelAll();
      setIsPopupVisible(false);
    }, [setIsPopupVisible]);

    const handleChangePoint = (item: any, tabKey: TabKey) => {
      onChangeActiveKey(item, tabKey);
      switchVisiblePopup(item);
    };

    return (
      <Modal
        key={`validation-analytics-popup-${point?.id}`}
        open={isPopupVisible}
        onCancel={handleCancel}
        cancelButtonProps={{ id: 'Analytics-Modal_Cancel' }}
        style={{
          paddingBottom: 0,
          top: 32,
          maxHeight: 'calc(100vh - 64px)',
          minHeight: '90vh',
          // minWidth: 1200,
        }}
        width={'96.5vw'}
        footer={[<></>]}
        className={styles.analyticsModal}
        title={
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className={styles.modalTabs}
            items={[
              {
                key: ITEM.VALIDATION,
                label: 'Validation',
              },
              {
                key: ITEM.ANALYTICS,
                label: 'Analytics',
              },
              {
                key: ITEM.REPORTS,
                label: 'Reports',
              },
            ]}
          />
        }
      >
        <div className={styles.tabContent}>
          <TabContentMemoized
            ref={analyticsRef}
            key={`validation-analytics-content-${point?.id}`}
            onChangeActiveKey={handleChangePoint}
            activePlotValidation={activePlotValidation}
            category={category}
            datasetId={datasetId}
            gridCellSize={gridCellSize}
            isTnw={isTnw}
            tabKey={activeTab}
            onChangePointLocation={onChangePointLocation}
            onMeshControl={onMeshControl}
            setActivePlotValidation={setActivePlotValidation}
            setDatasetIsOfflineWarningVisible={
              setDatasetIsOfflineWarningVisible
            }
            userGroupPermissions={userGroupPermissions}
            waterDepth={waterDepth}
            activePoint={activePoint}
            point={point}
          />
        </div>
      </Modal>
    );
  }
);
